import { createContext } from "preact";
import type { ComponentChildren } from "preact";
import { useContext, useId } from "preact/hooks";
import { cn } from "./cn";

// Wiring that a Field hands down to the single form control it wraps, so the
// <label> and the hint / error text are programmatically associated with it
// (screen readers otherwise announce an unnamed input).
type FieldControl = {
  id: string;
  describedBy?: string;
};

const FieldContext = createContext<FieldControl | null>(null);

// Resolve the id / aria-describedby for a control primitive. An explicit id on
// the control always wins; otherwise the enclosing Field's generated id is used.
export function useFieldControl(
  explicitId: string | undefined,
  explicitDescribedBy: string | undefined,
): { id: string | undefined; describedBy: string | undefined } {
  const ctx = useContext(FieldContext);
  const describedBy =
    [explicitDescribedBy, ctx?.describedBy].filter(Boolean).join(" ") ||
    undefined;
  return { id: explicitId ?? ctx?.id, describedBy };
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
  class: extraClass,
  htmlFor,
}: {
  label?: string;
  hint?: ComponentChildren;
  error?: string;
  required?: boolean;
  children: ComponentChildren;
  class?: string;
  // Pass only when the control carries its own explicit id; by default the
  // label is wired to the wrapped Input / Textarea / Select automatically.
  htmlFor?: string;
}) {
  const autoId = useId();
  const controlId = htmlFor ?? `${autoId}-control`;
  const messageId = `${autoId}-message`;
  const hasMessage = Boolean(error || hint);

  return (
    <div class={cn("grid gap-1.5", extraClass)}>
      {label && (
        <label
          htmlFor={controlId}
          class="text-xs font-medium text-fg-muted"
        >
          {label}
          {required && <span class="ml-0.5 text-danger-fg">*</span>}
        </label>
      )}
      <FieldContext.Provider
        value={{
          id: controlId,
          describedBy: hasMessage ? messageId : undefined,
        }}
      >
        {children}
      </FieldContext.Provider>
      {error ? (
        <p id={messageId} class="text-xs text-danger-fg">
          {error}
        </p>
      ) : hint ? (
        <p id={messageId} class="text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
